import { memo, useMemo, useState } from "react";
import { marked } from "marked";
import { AiAssistant, Chat, ChatMessage } from "@redelair-sa/intiza-ui";
import { SafeHtml } from "../../../components/SafeHtml";
import { TranslationService } from "../../../services/TranslationService";
import { formatDateTimeShort } from "../../../utils/FormatUtils";
import { useAssistantChat } from "../../../hooks/useAssistantChat";
import "./assistant.css";

const AssistantMarkdown = memo(({ content }: { content: string }) => {
  const html = useMemo(
    () => marked.parse(content, { async: false, breaks: true, gfm: true }),
    [content]
  );
  return <SafeHtml html={html} className="assistant-markdown" />;
});

const renderMessageContent = (message: ChatMessage) =>
  message.role === "assistant" ? (
    <AssistantMarkdown content={message.content} />
  ) : (
    message.content
  );

const formatConversationDate = (isoDate: string) =>
  formatDateTimeShort(new Date(isoDate));

const AssistantWelcome = () => {
  const [before, after] = TranslationService.translate.AIAssistantWelcome.split(
    "{topics}",
    2
  );
  return (
    <p>
      {before}
      <strong>{TranslationService.translate.AIAssistantWelcomeTopics}</strong>
      {after}
    </p>
  );
};

/** Botón "✨ Asistente" del header + drawer con el chat (GDC-2950 / GDC-5449). */
const AssistantButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const {
    messages,
    status,
    activityLabel,
    error,
    conversationId,
    conversations,
    conversationsLoading,
    send,
    openConversation,
    startNewConversation,
    refreshConversations,
    restoreConversation
  } = useAssistantChat();
  const translate = TranslationService.translate;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      restoreConversation();
    }
  };

  return (
    <AiAssistant
      label={translate.AIAssistantButton}
      drawerAriaLabel={translate.AIAssistantTitle}
      closeAriaLabel={translate.Close}
      title={translate.AIAssistantTitle}
      subtitle={translate.AIAssistantSubtitle}
      fullHeightBody
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Chat
        messages={messages}
        status={status}
        activityLabel={activityLabel}
        error={error}
        onSend={send}
        suggestions={[
          translate.AIAssistantSuggestion1,
          translate.AIAssistantSuggestion2,
          translate.AIAssistantSuggestion3
        ]}
        emptyState={<AssistantWelcome />}
        footerHint={translate.AIAssistantFooterHint}
        renderMessageContent={renderMessageContent}
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        activeConversationId={conversationId}
        onHistoryOpen={refreshConversations}
        onSelectConversation={openConversation}
        onNewConversation={startNewConversation}
        formatDate={formatConversationDate}
        labels={{
          placeholder: translate.AIAssistantPlaceholder,
          send: translate.AIAssistantSend,
          newConversation: translate.AIAssistantNewConversation,
          history: translate.AIAssistantHistory,
          back: translate.AIAssistantBack,
          untitledConversation: translate.AIAssistantUntitled,
          emptyHistory: translate.AIAssistantEmptyHistory,
          loadingHistory: translate.AIAssistantLoadingHistory,
          thinking: translate.AIAssistantThinking
        }}
      />
    </AiAssistant>
  );
};

export default AssistantButton;
